import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import BuildIcon from '@material-ui/icons/Build';
import PINValidator from './PINValidator';
import {
  parsePEMPrivateKey,
  parseDERPrivateKey,
  buildPIVImportAPDU,
  validateKeyData
} from './keyParser';
import { transceive } from './actions';

/**
 * PIV Key Import Dialog
 * Handles file upload, parsing, and import of private keys to PIV slots
 * Also supports generating keys on card
 */

// Key algorithms for on-card generation
const KEY_ALGORITHMS = [
  { value: '07', label: 'RSA-2048', description: 'Most compatible, good security (Recommended)' },
  { value: '11', label: 'ECC P-256', description: 'Modern, efficient, good security' },
  { value: '14', label: 'ECC P-384', description: 'High security elliptic curve' },
  { value: '05', label: 'RSA-1024', description: 'Legacy, not recommended' },
  { value: '06', label: 'RSA-3072', description: 'High security (requires firmware support)' },
  { value: '16', label: 'RSA-4096', description: 'Maximum security (requires firmware support)' }
];

const PIVKeyImportDialog = ({ open, onClose, slot, slotName, dispatch, onImportSuccess }) => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Import tab states
  const [file, setFile] = useState(null);
  const [keyInfo, setKeyInfo] = useState(null);
  const [pin, setPin] = useState('');
  const [pinPolicy, setPinPolicy] = useState('01'); // Once per session
  const [touchPolicy, setTouchPolicy] = useState('00'); // Never
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Generate tab states
  const [algorithm, setAlgorithm] = useState('07'); // Default RSA-2048
  const [genPin, setGenPin] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genError, setGenError] = useState('');
  const [genSuccess, setGenSuccess] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setKeyInfo(null);

    try {
      const fileContent = await selectedFile.text();

      // Detect file format and parse
      let parsedKey;
      if (selectedFile.name.endsWith('.pem') || fileContent.includes('BEGIN')) {
        parsedKey = await parsePEMPrivateKey(fileContent);
      } else if (selectedFile.name.endsWith('.der')) {
        const arrayBuffer = await selectedFile.arrayBuffer();
        parsedKey = await parseDERPrivateKey(arrayBuffer);
      } else {
        // Try PEM first, then DER
        try {
          parsedKey = await parsePEMPrivateKey(fileContent);
        } catch (e) {
          const arrayBuffer = await selectedFile.arrayBuffer();
          parsedKey = await parseDERPrivateKey(arrayBuffer);
        }
      }

      // Validate key
      const validation = validateKeyData(parsedKey);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      setKeyInfo(parsedKey);
    } catch (error) {
      console.error('File parsing error:', error);
      setError('Failed to parse key file: ' + error.message);
    }
  };

  const handleImport = async () => {
    if (!keyInfo || !pin) {
      setError('Please select a file and enter PIN');
      return;
    }

    setImporting(true);
    setProgress(0);
    setError('');

    try {
      // Step 1: Select PIV applet
      setProgress(20);
      await dispatch(transceive('00A4040009A00000030800001000'));

      // Step 2: Verify PIN
      setProgress(40);
      const pinHex = pin.split('').map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
      const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
      const verifyRes = await dispatch(transceive(verifyAPDU));

      if (!verifyRes.endsWith('9000')) {
        const sw = verifyRes.slice(-4);
        if (sw.startsWith('63C')) {
          const remaining = parseInt(sw.slice(3), 16);
          throw new Error(`PIN verification failed. ${remaining} attempts remaining.`);
        }
        throw new Error('PIN verification failed');
      }

      // Step 3: Build and send import APDU
      setProgress(60);
      const options = {
        pinPolicy: parseInt(pinPolicy, 16),
        touchPolicy: parseInt(touchPolicy, 16)
      };
      const importAPDU = buildPIVImportAPDU(keyInfo, slot, options);
      const response = await dispatch(transceive(importAPDU));

      setProgress(80);

      if (response.endsWith('9000')) {
        setProgress(100);
        setSuccess(true);

        // Notify parent
        if (onImportSuccess) {
          onImportSuccess(slot, keyInfo);
        }
      } else {
        throw new Error(`Import failed with status: ${response.slice(-4)}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      setError(error.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing && !generating) {
      // Reset all states
      setTabValue(0);
      setFile(null);
      setKeyInfo(null);
      setPin('');
      setError('');
      setSuccess(false);
      setProgress(0);
      setAlgorithm('07');
      setGenPin('');
      setGenerating(false);
      setGenProgress(0);
      setGenError('');
      setGenSuccess(false);
      onClose();
    }
  };

  const handleGenerate = async () => {
    if (!genPin) {
      setGenError('Please enter PIN');
      return;
    }

    setGenerating(true);
    setGenProgress(0);
    setGenError('');

    try {
      // Step 1: Select PIV applet
      setGenProgress(20);
      await dispatch(transceive('00A4040009A00000030800001000'));

      // Step 2: Verify PIN
      setGenProgress(40);
      const pinHex = genPin.split('').map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
      const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
      const verifyRes = await dispatch(transceive(verifyAPDU));

      if (!verifyRes.endsWith('9000')) {
        const sw = verifyRes.slice(-4);
        if (sw.startsWith('63C')) {
          const remaining = parseInt(sw.slice(3), 16);
          throw new Error(`PIN verification failed. ${remaining} attempts remaining.`);
        }
        throw new Error('PIN verification failed');
      }

      // Step 3: Generate key on card
      setGenProgress(60);
      // APDU: 00 47 00 [slot] 05 AC 03 [algorithm] 00
      const genAPDU = `0047000${slot}05AC03${algorithm}00`;
      const response = await dispatch(transceive(genAPDU));

      setGenProgress(80);

      if (response.endsWith('9000')) {
        setGenProgress(100);
        setGenSuccess(true);

        // Notify parent
        if (onImportSuccess) {
          onImportSuccess(slot, null);
        }
      } else {
        throw new Error(`Key generation failed with status: ${response.slice(-4)}`);
      }
    } catch (error) {
      console.error('Generation error:', error);
      setGenError(error.message || 'Key generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear errors when switching tabs
    setError('');
    setGenError('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={importing || generating}
    >
      <DialogTitle>
        Configure Key for Slot {slot} - {slotName}
      </DialogTitle>
      <DialogContent>
        {/* Tabs */}
        <Box style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="key configuration tabs">
            <Tab label="Import External Key" icon={<CloudUploadIcon />} />
            <Tab label="Generate on Card" icon={<BuildIcon />} />
          </Tabs>
        </Box>

        {/* Tab 0: Import External Key */}
        {tabValue === 0 && (
          <>
            {!success ? (
              <>
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  <AlertTitle>Import External Key</AlertTitle>
                  Select a private key file (PEM or DER format) to import to this slot. The private key will be securely transferred to your device.
                </Alert>

                {/* File Upload */}
                <Box style={{ marginBottom: 24 }}>
                  <input
                    accept=".pem,.der,.key"
                    style={{ display: 'none' }}
                    id="key-file-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={importing}
                  />
                  <label htmlFor="key-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={importing}
                    >
                      {file ? file.name : 'Select Key File (.pem, .der)'}
                    </Button>
                  </label>
                </Box>

            {/* Key Information */}
            {keyInfo && (
              <Paper elevation={2} style={{ padding: 16, marginBottom: 16, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Key Information:
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell>{keyInfo.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Size</strong></TableCell>
                      <TableCell>{keyInfo.size} bits</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Algorithm</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={
                            keyInfo.algorithm === '07' ? 'RSA-2048' :
                            keyInfo.algorithm === '05' ? 'RSA-3072' :
                            keyInfo.algorithm === '16' ? 'RSA-4096' :
                            keyInfo.algorithm === '11' ? 'ECC P-256' :
                            keyInfo.algorithm === '14' ? 'ECC P-384' :
                            'Unknown'
                          }
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            )}

            {/* PIN Input */}
            <PINValidator
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              label="PIV PIN"
              type="PIV"
              dispatch={dispatch}
              disabled={importing}
            />

            {/* Advanced Options */}
            <Box style={{ marginTopadding: 16 * 8 }}>
              <Typography variant="subtitle2" gutterBottom>
                Advanced Options:
              </Typography>

              <FormControl fullWidth style={{ marginBottom: 8 }}>
                <InputLabel>PIN Policy</InputLabel>
                <Select
                  value={pinPolicy}
                  onChange={(e) => setPinPolicy(e.target.value)}
                  label="PIN Policy"
                  disabled={importing}
                >
                  <MenuItem value="00">Never (No PIN required)</MenuItem>
                  <MenuItem value="01">Once per session (Recommended)</MenuItem>
                  <MenuItem value="02">Always (PIN for every use)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Touch Policy</InputLabel>
                <Select
                  value={touchPolicy}
                  onChange={(e) => setTouchPolicy(e.target.value)}
                  label="Touch Policy"
                  disabled={importing}
                >
                  <MenuItem value="00">Never (No touch required)</MenuItem>
                  <MenuItem value="01">Always (Touch for every use)</MenuItem>
                  <MenuItem value="02">Cached (Touch once per session)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Progress Bar */}
            {importing && (
              <Box style={{ marginTopadding: 16 * 8 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" align="center" style={{ marginTopadding: 8 * 8 }}>
                  {progress < 40 ? 'Verifying PIN...' :
                   progress < 60 ? 'Importing key...' :
                   progress < 100 ? 'Finalizing...' : 'Complete!'}
                </Typography>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error" style={{ marginTopadding: 16 * 8 }} icon={<ErrorIcon />}>
                <AlertTitle>Import Failed</AlertTitle>
                {error}
              </Alert>
            )}
          </>
        ) : (
          /* Success Display */
          <Box style={{ textAlign: 'center', paddingTop: 3 * 8, paddingBottom: 3 * 8 }}>
            <CheckCircleIcon color="primary" style={{ fontSize: 64, marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>
              Key Imported Successfully!
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your private key has been imported to Slot {slot} ({slotName}).
            </Typography>

            <Alert severity="info" style={{ marginTopadding: 24 * 8, textAlign: 'left' }}>
              <AlertTitle>Next Steps</AlertTitle>
              <Typography variant="body2">
                • Generate a self-signed certificate for this key<br />
                • Or generate a CSR to get a certificate from a CA<br />
                • Remember to backup your private key file securely
              </Typography>
            </Alert>
          </Box>
        )}
          </>
        )}

        {/* Tab 1: Generate on Card */}
        {tabValue === 1 && (
          <>
            {!genSuccess ? (
              <>
                <Alert severity="success" style={{ marginBottom: 16 }}>
                  <AlertTitle>Generate Key on Card</AlertTitle>
                  Generate a new cryptographic key pair directly on your device. The private key will never leave the secure element.
                </Alert>

                <Alert severity="warning" style={{ marginBottom: 24 }}>
                  <AlertTitle>⚠️ Important: No Backup Possible</AlertTitle>
                  Keys generated on the card cannot be exported or backed up. If you lose your device, you will lose access to this key. For backup capability, use "Import External Key" instead.
                </Alert>

                {/* Algorithm Selection */}
                <FormControl component="fieldset" style={{ marginBottom: 24, width: '100%' }}>
                  <FormLabel component="legend">Select Key Algorithm</FormLabel>
                  <RadioGroup
                    value={algorithm}
                    onChange={(e) => setAlgorithm(e.target.value)}
                  >
                    {KEY_ALGORITHMS.map((alg) => (
                      <FormControlLabel
                        key={alg.value}
                        value={alg.value}
                        control={<Radio disabled={generating} />}
                        label={
                          <Box>
                            <Typography variant="body1">
                              {alg.label}
                              {alg.value === '07' && <Chip label="Recommended" size="small" color="primary" style={{ ml: 1 }} />}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {alg.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {/* PIN Input */}
                <PINValidator
                  value={genPin}
                  onChange={(e) => setGenPin(e.target.value)}
                  label="PIV PIN"
                  type="PIV"
                  dispatch={dispatch}
                  disabled={generating}
                />

                {/* Progress Bar */}
                {generating && (
                  <Box style={{ marginTopadding: 16 * 8 }}>
                    <LinearProgress variant="determinate" value={genProgress} />
                    <Typography variant="body2" align="center" style={{ marginTopadding: 8 * 8 }}>
                      {genProgress < 40 ? 'Verifying PIN...' :
                       genProgress < 60 ? 'Generating key pair...' :
                       genProgress < 100 ? 'Finalizing...' : 'Complete!'}
                    </Typography>
                  </Box>
                )}

                {/* Error Display */}
                {genError && (
                  <Alert severity="error" style={{ marginTopadding: 16 * 8 }} icon={<ErrorIcon />}>
                    <AlertTitle>Generation Failed</AlertTitle>
                    {genError}
                  </Alert>
                )}
              </>
            ) : (
              /* Success Display */
              <Box style={{ textAlign: 'center', paddingTop: 3 * 8, paddingBottom: 3 * 8 }}>
                <CheckCircleIcon color="primary" style={{ fontSize: 64, marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom>
                  Key Generated Successfully!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  A new key pair has been generated on your device for Slot {slot} ({slotName}).
                </Typography>

                <Alert severity="info" style={{ marginTopadding: 24 * 8, textAlign: 'left' }}>
                  <AlertTitle>Next Steps</AlertTitle>
                  <Typography variant="body2">
                    • Generate a self-signed certificate for this key<br />
                    • Or generate a CSR to get a certificate from a CA<br />
                    • The private key is securely stored on your device
                  </Typography>
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {tabValue === 0 && (
          <>
            {!success ? (
              <>
                <Button onClick={handleClose} disabled={importing}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  variant="contained"
                  disabled={!keyInfo || !pin || importing}
                  startIcon={<CloudUploadIcon />}
                >
                  {importing ? 'Importing...' : 'Import Key'}
                </Button>
              </>
            ) : (
              <Button onClick={handleClose} variant="contained">
                Close
              </Button>
            )}
          </>
        )}
        {tabValue === 1 && (
          <>
            {!genSuccess ? (
              <>
                <Button onClick={handleClose} disabled={generating}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  variant="contained"
                  disabled={!genPin || generating}
                  startIcon={<BuildIcon />}
                  color="primary"
                >
                  {generating ? 'Generating...' : 'Generate Key'}
                </Button>
              </>
            ) : (
              <Button onClick={handleClose} variant="contained">
                Close
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PIVKeyImportDialog;
